/**
 * Control-plane client the UI actually imports — dispatches to the dev or prod
 * implementation by deployment mode (M5.4).
 *
 * Both back-ends expose the identical surface (`requestSpectatorTicket`,
 * `joinAsPlayer`) returning the same {@link HostTicket}/{@link PlayerTicket}
 * shapes, so the pages stay mode-agnostic: they import from here, and the build's
 * `VITE_DEPLOY_MODE` decides whether calls hit `/dev/*` (local) or `/api/*`
 * (deployed, same-origin behind CloudFront). The chosen module is picked ONCE at
 * load, not per call.
 */

import { IS_PROD_DEPLOY } from '../config';
import type { HostTicket, PlayerTicket } from './api-types';
import * as devApi from './dev-api';
import * as prodApi from './prod-api';

export type { HostTicket, PlayerTicket } from './api-types';

const api = IS_PROD_DEPLOY ? prodApi : devApi;

/**
 * Mint a host/presentation ticket. `gameId` targets a SPECIFIC game — resolved from a
 * host-shared join code via {@link resolveGameByCode} (M8) — so a presentation screen
 * binds to the host's game, not the demo game (#51). Omitted → the prod client falls
 * back to the demo game (the host path, which already knows its own gameId, and the dev
 * single game). Dev ignores the id (one game).
 */
export function requestSpectatorTicket(
  role: 'host' | 'presentation',
  gameId?: string,
): Promise<HostTicket> {
  return api.requestSpectatorTicket(role, gameId);
}

/** Resolve a host-shared join code → gameId (M8 join-by-code). */
export function resolveGameByCode(code: string): Promise<string> {
  return api.resolveGameByCode(code);
}

export function joinAsPlayer(
  name: string,
  identity?: { guestToken?: string; playerId?: string },
  gameId?: string,
  joinCode?: string,
): Promise<PlayerTicket> {
  return api.joinAsPlayer(name, identity, gameId, joinCode);
}
